import { useState, useRef, useEffect, useCallback } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import './App.css';
import MobileStartVisit from './components/MobileStartVisit';
import { blobToBase64, base64ToBlob } from './utils/indexedDB';
import { apiUrl } from './config';
import PrivacyPolicy from './PrivacyPolicy';
import TermsConditions from './TermsConditions';
import LandingPage from './pages/LandingPage';
import ProfilePage from './pages/ProfilePage';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import EmbeddedCheckout from './components/EmbeddedCheckout';
import Demo from './Demo';
import { PaymentSuccess } from './pages/PaymentSuccess';
import { PaymentFailed } from './pages/PaymentFailed';
import { authApi, billingApi, soapApi, patientApi } from './services/api';
import { PatientDashboard } from './features/dashboard';
import { AuthScreen } from './features/auth';
import {
  DeleteNoteModal,
  ConfirmCloseModal,
  SaveSuccessModal,
  GenericSuccessModal,
  GenericErrorModal,
  SessionExpiredModal,
  EditPatientModal,
  AddPatientModal,
  PasswordChangeModal,
  CancelSubscriptionModal,
  NotificationModal,
} from './components/modals';
import { SOAPNoteDisplay } from './components/soap/SOAPNoteDisplay';
import { DoctorOnboarding } from './components/DoctorOnboarding';
import { ProgressBar } from './components/ProgressBar';

// Extracted components:
// - FormattedText → components/common/FormattedText
// - PaymentSuccess → pages/PaymentSuccess
// - PaymentFailed → pages/PaymentFailed
// - PatientDashboard → features/dashboard/PatientDashboard
// - AuthScreen → features/auth/AuthScreen
// - DeleteNoteModal, ConfirmCloseModal, SaveSuccessModal,
//   GenericSuccessModal, GenericErrorModal, EditPatientModal,
//   AddPatientModal → components/modals/
//
// Now using API service layer (authApi, billingApi, soapApi, patientApi) instead of raw fetch()


function MainApp() {
  const navigate = useNavigate();
  // Page navigation states
  const [currentPage, setCurrentPage] = useState('main'); // 'main', 'privacy', 'terms'

  // Auth states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginMode, setLoginMode] = useState('doctor'); // 'doctor' or 'patient'
  const [showSignup, setShowSignup] = useState(false); // Toggle between login and signup

  // Check for mode query parameter on mount (for email verification redirects)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    if (mode === 'doctor' || mode === 'patient') {
      setLoginMode(mode);
    }
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [accessError, setAccessError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [currentUser, setCurrentUser] = useState(null); // HIPAA/PIPEDA compliant: server-verified user data
  const [isFirstLogin, setIsFirstLogin] = useState(false); // True only on the user's very first login
  const [showProfileDropdown, setShowProfileDropdown] = useState(false); // Profile menu dropdown state
  const [showPasswordChangeModal, setShowPasswordChangeModal] = useState(false); // Password change confirmation modal
  const [showPricingModal, setShowPricingModal] = useState(false); // Pricing/subscription modal
  const [notificationModal, setNotificationModal] = useState({ show: false, type: 'success', title: '', message: '' }); // Notification modal
  const [subscriptionStatus, setSubscriptionStatus] = useState(null); // User's subscription status
  const [promoCode, setPromoCode] = useState(''); // Promo code input
  const [promoCodeError, setPromoCodeError] = useState(''); // Promo code validation error
  const [isApplyingPromo, setIsApplyingPromo] = useState(false); // Loading state for promo code validation

  // App states
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false); // Prevent duplicate checkout clicks
  const [checkoutPlan, setCheckoutPlan] = useState(null); // Track which plan is starting
  const [showEmbeddedCheckout, setShowEmbeddedCheckout] = useState(false); // Show embedded checkout modal
  const [isCancelingSubscription, setIsCancelingSubscription] = useState(false); // Cancel subscription action state
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] = useState(false); // Cancel subscription confirmation modal
  const [isRecording, setIsRecording] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentGeneratingStage, setCurrentGeneratingStage] = useState(null); // Track which stage is currently processing
  const [isPlayingFile, setIsPlayingFile] = useState(false);
  const [fileAudioDuration, setFileAudioDuration] = useState(0);
  const [fileAudioCurrentTime, setFileAudioCurrentTime] = useState(0);
  const fileAudioRef = useRef(null);
  const [soapNote, setSoapNote] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [patientName, setPatientName] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null); // Selected patient object with ID
  const [patients, setPatients] = useState([]); // List of doctor's patients
  const [showPatientModal, setShowPatientModal] = useState(false); // Show add patient modal
  const [patientSearchQuery, setPatientSearchQuery] = useState(''); // Search query for filtering patients
  const [showPatientDropdown, setShowPatientDropdown] = useState(false); // Show/hide dropdown
  const [newPatientData, setNewPatientData] = useState({ fullName: '', email: '', dateOfBirth: '', phone: '' });
  const [patientModalError, setPatientModalError] = useState(''); // Error specific to patient modal
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentDateError, setAppointmentDateError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedSoapNote, setEditedSoapNote] = useState(null);
  const [isRegeneratingSupport, setIsRegeneratingSupport] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [soapHistory, setSoapHistory] = useState([]);
  const [currentNoteId, setCurrentNoteId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSavingHistory, setIsSavingHistory] = useState(false);
  const [isLoadingHistoryNote, setIsLoadingHistoryNote] = useState(false);
  const [patientNotes, setPatientNotes] = useState([]); // Patient's SOAP notes
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [accountClaimed, setAccountClaimed] = useState(false); // Whether patient has claimed their account
  const [claimingAccount, setClaimingAccount] = useState(false);
  const [editingCodeIndex, setEditingCodeIndex] = useState(null); // Index of billing code being edited
  const [editedCodeValue, setEditedCodeValue] = useState(''); // Temporary value for code being edited
  const [editedCodeDescription, setEditedCodeDescription] = useState(''); // Temporary value for description
  const [editedCodeTiming, setEditedCodeTiming] = useState(''); // Temporary value for timing
  const [isNewCode, setIsNewCode] = useState(false); // Track if the code being edited is newly added
  const [patientFilterDateFrom, setPatientFilterDateFrom] = useState(''); // Patient portal date filter
  const [patientFilterDateTo, setPatientFilterDateTo] = useState(''); // Patient portal date filter
  const [showDeleteNoteModal, setShowDeleteNoteModal] = useState(false); // Show delete note confirmation modal
  const [noteToDelete, setNoteToDelete] = useState(null); // Note being deleted
  const [showSaveSuccessModal, setShowSaveSuccessModal] = useState(false); // Show save success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false); // Generic success modal
  const [successMessage, setSuccessMessage] = useState(''); // Success message content
  const [showErrorModal, setShowErrorModal] = useState(false); // Generic error modal
  const [errorMessage, setErrorMessage] = useState(''); // Error message content
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false); // Session expired modal
  const [showEditPatientModal, setShowEditPatientModal] = useState(false); // Show edit patient modal
  const [patientToEdit, setPatientToEdit] = useState(null); // Patient being edited
  const [editPatientData, setEditPatientData] = useState({ phone: '' }); // Edit patient form data
  const [editPatientError, setEditPatientError] = useState(''); // Error specific to edit patient modal
  const [showSignupSuccessModal, setShowSignupSuccessModal] = useState(false); // Show signup success modal
  const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false); // Show close confirmation modal for unsaved SOAP notes
  const [isDragging, setIsDragging] = useState(false); // Drag-and-drop state

  // Derived subscription state helpers
  const subscriptionState = subscriptionStatus?.subscription;
  const subscriptionStatusValue = subscriptionState?.status;
  const isSubscriptionEnded = ['expired', 'canceled', 'unpaid'].includes(subscriptionStatusValue);
  const isSubscriptionInTrial = subscriptionStatusValue === 'trialing';

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const fileInputRef = useRef(null);
  const recordingStartTimeRef = useRef(null);
  const isLoadingFromHistoryRef = useRef(false); // Flag to prevent clearing when loading from history
  const abortControllerRef = useRef(null); // AbortController for transcription requests
  const stageTimersRef = useRef([]); // Track stage progression timers for cleanup

  const handleStartTrial = async (plan) => {
    if (isCheckoutLoading) return;

    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      setAccessError('Please log in to start your trial.');
      setShowSignup(false);
      navigate('/login');
      return;
    }

    setCheckoutPlan(plan);
    setIsCheckoutLoading(true);

    try {
      // If subscription ended (expired/canceled/unpaid), go to checkout instead of trial
      const isTrialExpired = isSubscriptionEnded;

      if (isTrialExpired) {
        // Show embedded checkout modal instead of redirecting
        setShowPricingModal(false);
        setShowEmbeddedCheckout(true);
        setIsCheckoutLoading(false);
        // Don't reset checkoutPlan here - modal needs it
        return;
      } else {
        // Start free trial (no credit card required)
        const data = await billingApi.startTrial(plan);

        if (data.success) {
          // Trial started successfully - close modal and show success
          setShowPricingModal(false);
          setAccessError(null);

          // Refresh subscription status
          const statusData = await billingApi.getStatus();
          if (statusData.success) {
            setSubscriptionStatus(statusData);
          }

          setNotificationModal({
            show: true,
            type: 'success',
            title: 'Trial Started!',
            message: `Your 14-day free trial of the ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan has started. No credit card required!`
          });
        } else {
          setAccessError(data.error || 'Unable to start trial. Please try again.');
        }
      }
    } catch (err) {
      console.error('Trial start error:', err);
      setAccessError('Unable to start trial. Please try again.');
    } finally {
      setIsCheckoutLoading(false);
      // Only reset checkoutPlan if we're not showing embedded checkout
      // (embedded checkout needs the plan value)
      const isTrialExpired = isSubscriptionEnded;
      if (!isTrialExpired) {
        setCheckoutPlan(null);
      }
    }
  };

  // HIPAA/PIPEDA Compliant: Fetch current user from backend on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const accessToken = localStorage.getItem('accessToken');

      if (!accessToken) {
        return;
      }

      try {
        const data = await authApi.me();

        if (data.success) {
          setCurrentUser(data.user);
          setIsAuthenticated(true);
          // Update last activity timestamp on successful login
          localStorage.setItem('lastActivity', Date.now().toString());

          // Reschedule the refresh timer based on remaining token lifetime.
          // window.tokenRefreshTimer is lost on page reload, so we always
          // re-derive it here from the JWT exp claim.
          try {
            const payload = JSON.parse(atob(accessToken.split('.')[1]));
            const secondsRemaining = payload.exp - Math.floor(Date.now() / 1000);
            const refreshIn = Math.max(0, secondsRemaining - 120); // 2 min before expiry
            scheduleTokenRefresh(refreshIn);
          } catch (e) {
            // Malformed token — 401 handler will catch it on next request
          }
        }
      } catch (err) {
        console.error('Error fetching current user:', err);
      }
    };

    fetchCurrentUser();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch subscription status
  const fetchSubscriptionStatus = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('[Subscription] Not fetching: not authenticated');
      return;
    }

    try {
      console.log('[Subscription] Fetching status...');
      const data = await billingApi.getStatus();

      console.log('[Subscription] Response data:', data);
      if (data.success) {
        setSubscriptionStatus(data);
        console.log('[Subscription] Status updated:', data);
      } else {
        console.warn('[Subscription] Response not successful:', data);
      }
    } catch (err) {
      console.error('[Subscription] Error fetching subscription status:', err);
    }
  }, [isAuthenticated]);

  // Fetch subscription status
  useEffect(() => {
    fetchSubscriptionStatus();
  }, [fetchSubscriptionStatus]);

  const handleCancelSubscription = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken || !subscriptionStatus?.has_subscription || isSubscriptionEnded) {
      return;
    }

    setIsCancelingSubscription(true);
    try {
      const data = await billingApi.cancelSubscription();
      if (data.success) {
        setShowProfileDropdown(false);
        await fetchSubscriptionStatus();
        setNotificationModal({
          show: true,
          type: 'success',
          title: 'Subscription canceled',
          message: 'Your subscription has been canceled. You can upgrade again anytime.',
        });
      } else {
        setNotificationModal({
          show: true,
          type: 'error',
          title: 'Unable to cancel',
          message: data.error || 'Please try again.',
        });
      }
    } catch (err) {
      console.error('Cancel subscription error:', err);
      setNotificationModal({
        show: true,
        type: 'error',
        title: 'Unable to cancel',
        message: 'Please try again.',
      });
    } finally {
      setIsCancelingSubscription(false);
    }
  };

  // Listen for logout events from other tabs/windows (e.g., password reset)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'logout-event') {
        // Another tab/window logged out (e.g., password was reset)
        console.log('Logout event detected from another tab');
        handleLogout();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Listen for 401 token-expired events fired by the API client (covers all requests, including audio uploads)
  useEffect(() => {
    const handleTokenExpired = () => {
      // Only act if the user is currently authenticated — avoids double-triggering on logout
      if (localStorage.getItem('accessToken')) {
        console.log('Token expired event received — showing session expired modal');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('lastActivity');
        setShowSessionExpiredModal(true);
      }
    };

    window.addEventListener('auth:token-expired', handleTokenExpired);
    return () => window.removeEventListener('auth:token-expired', handleTokenExpired);
  }, []);

  // Inactivity timeout: Auto-logout after 60 minutes of inactivity (aligned with access token)
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutes in milliseconds

    // Update last activity timestamp on user interactions
    const updateActivity = () => {
      localStorage.setItem('lastActivity', Date.now().toString());
    };

    // Check for inactivity every minute
    const checkInactivity = () => {
      const lastActivity = localStorage.getItem('lastActivity');
      if (!lastActivity) {
        // No activity recorded, set it now
        localStorage.setItem('lastActivity', Date.now().toString());
        return;
      }

      const timeSinceLastActivity = Date.now() - parseInt(lastActivity);

      if (timeSinceLastActivity > INACTIVITY_TIMEOUT) {
        // User has been inactive for too long — clear tokens and show modal.
        // Do NOT set isAuthenticated(false) here; that would unmount the modal
        // before it renders. handleLogout() is called when user clicks "Go to Login".
        console.log('Auto-logout due to inactivity');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('lastActivity');
        setShowSessionExpiredModal(true);
      }
    };

    // Track user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity);
    });

    // Check for inactivity every minute
    const inactivityCheckInterval = setInterval(checkInactivity, 60000);

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      clearInterval(inactivityCheckInterval);
    };
  }, [isAuthenticated]);

  // Fetch doctor's patients when authenticated
  useEffect(() => {
    const fetchPatients = async () => {
      const accessToken = localStorage.getItem('accessToken');

      if (!accessToken || !currentUser || currentUser.role !== 'doctor') {
        return;
      }

      try {
        const data = await patientApi.getAll();
        if (data.success) {
          setPatients(data.patients);
        }
      } catch (err) {
        console.error('Error fetching patients:', err);
      }
    };

    fetchPatients();
  }, [currentUser]);

  // Fetch patient notes when logged in as patient
  useEffect(() => {
    const fetchPatientNotes = async () => {
      const accessToken = localStorage.getItem('accessToken');

      if (!accessToken || !currentUser || currentUser.role !== 'patient') {
        setPatientNotes([]);
        return;
      }

      try {
        setLoadingNotes(true);
        const data = await patientApi.getMyNotes();
        if (data.success) {
          setPatientNotes(data.notes || []);
        }
      } catch (err) {
        console.error('Error fetching patient notes:', err);
      } finally {
        setLoadingNotes(false);
      }
    };

    fetchPatientNotes();
  }, [currentUser]);

  // Cleanup recording timer on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  // Clear SOAP note when patient or appointment date changes
  useEffect(() => {
    // Don't clear if we're loading from history
    if (isLoadingFromHistoryRef.current) {
      isLoadingFromHistoryRef.current = false; // Reset flag
      return;
    }

    if (soapNote) {
      setSoapNote(null);
      setEditedSoapNote(null);
      setIsEditing(false);
      setIsSaved(false);
      setHasUnsavedChanges(false);
      setCurrentNoteId(null);
      setSelectedFile(null);
      setRecordedAudio(null);
      setRecordingDuration(0);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPatient?.id, appointmentDate]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      return;
    }
    const pagePath = currentPage === 'main' ? '/' : `/${currentPage}`;
    window.gtag('event', 'page_view', {
      page_title: `Algodentra - ${currentPage}`,
      page_path: pagePath
    });
  }, [currentPage]);

  const trackAnalyticsEvent = (eventName, params = {}) => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
      return;
    }
    window.gtag('event', eventName, params);
  };

  const handleDemoCtaClick = () => {
    trackAnalyticsEvent('demo_cta_click', {
      event_category: 'cta',
      event_label: 'Book Demo Button'
    });
    window.Calendly.initPopupWidget({ url: 'https://calendly.com/gabikir1999/30min' });
    return false;
  };

  const handleStartRecording = async () => {
    if (!isRecording) {
      trackAnalyticsEvent('start_recording_click', {
        event_category: 'audio',
        event_label: 'Start Recording Button'
      });

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorderRef.current = mediaRecorder;
        audioChunksRef.current = [];

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data);
          }
        };

        mediaRecorder.onstop = () => {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

          // Calculate actual duration from start time
          const actualDuration = recordingStartTimeRef.current
            ? Math.floor((Date.now() - recordingStartTimeRef.current) / 1000)
            : 0;

          // Clear timer first
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }

          // Validate recording is not empty (minimum 1KB and at least 2 seconds)
          if (audioBlob.size < 1000 || actualDuration < 2) {
            setError('Recording is too short or empty. Please record at least 2 seconds of audio.');
            // Stop all tracks
            stream.getTracks().forEach(track => track.stop());
            setRecordingDuration(0);
            recordingStartTimeRef.current = null;
            return;
          }

          const audioUrl = URL.createObjectURL(audioBlob);
          setRecordedAudio({
            url: audioUrl,
            blob: audioBlob,
            duration: actualDuration
          });
          setError(null); // Clear any previous errors

          // Stop all tracks
          stream.getTracks().forEach(track => track.stop());

          // Reset recording duration display
          setRecordingDuration(0);
          recordingStartTimeRef.current = null;
        };

        mediaRecorder.start();
        setIsRecording(true);
        setRecordingDuration(0);
        recordingStartTimeRef.current = Date.now(); // Store start time

        // Start timer
        timerIntervalRef.current = setInterval(() => {
          setRecordingDuration(prev => prev + 1);
        }, 1000);

      } catch (error) {
        console.error('Error accessing microphone:', error);
        setErrorMessage('Could not access microphone. Please ensure you have granted permission.');
        setShowErrorModal(true);
      }
    } else {
      // Stop recording
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      }
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file is not empty (minimum 1KB)
      if (file.size < 1000) {
        setError('Audio file is too small or empty. Please record or upload a valid audio file.');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Validate file size is not too large (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        setError('Audio file is too large. Maximum file size is 100MB.');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      setSelectedFile(file);
      setError(null); // Clear any previous errors
    }
  };

  const handleRemoveFile = () => {
    // Abort transcription if it's in progress
    if (isGenerating && abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Clear stage progression timers
    if (stageTimersRef.current.length > 0) {
      stageTimersRef.current.forEach(timer => clearTimeout(timer));
      stageTimersRef.current = [];
    }

    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (fileAudioRef.current) {
      fileAudioRef.current.pause();
      fileAudioRef.current.currentTime = 0;
    }
    setIsPlayingFile(false);
    setFileAudioCurrentTime(0);

    // Clear generating state and progress bar
    setIsGenerating(false);
    setCurrentGeneratingStage(null);
  };

  // Handle file audio playback
  const handlePlayFile = () => {
    if (!fileAudioRef.current || !selectedFile) return;

    if (isPlayingFile) {
      fileAudioRef.current.pause();
      setIsPlayingFile(false);
    } else {
      fileAudioRef.current.play().catch(err => {
        console.error('Error playing audio:', err);
        setError('Unable to play audio file');
      });
      setIsPlayingFile(true);
    }
  };

  // Update current time as audio plays
  const handleFileAudioTimeUpdate = () => {
    if (fileAudioRef.current) {
      setFileAudioCurrentTime(fileAudioRef.current.currentTime);
    }
  };

  // Handle audio finished playing
  const handleFileAudioEnded = () => {
    setIsPlayingFile(false);
  };

  // Load audio metadata
  const handleFileAudioLoadedMetadata = () => {
    if (fileAudioRef.current) {
      setFileAudioDuration(fileAudioRef.current.duration);
    }
  };

  // Format duration for display
  const formatAudioTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // Drag-and-drop event handlers
  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      // Validate file type (must be audio) - check both MIME type and file extension
      const audioExtensions = ['.mp3', '.wav', '.m4a', '.aac', '.ogg', '.flac', '.wma', '.webm', '.opus', '.aiff'];
      const fileName = file.name.toLowerCase();
      const hasAudioExtension = audioExtensions.some(ext => fileName.endsWith(ext));
      const hasAudioMimeType = file.type.startsWith('audio/') || file.type === 'video/webm'; // webm can be audio

      if (!hasAudioExtension && !hasAudioMimeType) {
        setError('Please upload an audio file (mp3, wav, m4a, webm, etc.).');
        return;
      }

      // Validate file is not empty (minimum 1KB)
      if (file.size < 1000) {
        setError('Audio file is too small or empty. Please record or upload a valid audio file.');
        return;
      }

      // Validate file size is not too large (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        setError('Audio file is too large. Maximum file size is 100MB.');
        return;
      }

      setSelectedFile(file);
      setError(null); // Clear any previous errors
    }
  };

  const handleNewRecording = () => {
    // Clear all recording/SOAP related state
    setRecordedAudio(null);
    setSelectedFile(null);
    setSoapNote(null);
    setError(null);
    setIsEditing(false);
    setEditedSoapNote(null);
    setCurrentNoteId(null);

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Clear any active timers
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    setRecordingDuration(0);
    recordingStartTimeRef.current = null;
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCopySOAP = () => {
    if (!soapNote || !soapNote.soap_note) return;

    const noteToUse = editedSoapNote || soapNote.soap_note;
    const { subjective, objective, assessment, plan } = noteToUse;

    // Build header with patient info
    let header = 'SOAP NOTE';
    if (patientName) {
      header += `\nPatient: ${patientName}`;
    }
    if (appointmentDate) {
      // Parse date string as local timezone to avoid timezone offset issues
      const [year, month, day] = appointmentDate.split('-');
      const formattedDate = new Date(year, month - 1, day).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      header += `\nDate: ${formattedDate}`;
    }
    header += '\n';

    let soapText = `${header}

SUBJECTIVE
${subjective || 'N/A'}

OBJECTIVE
${objective || 'N/A'}

ASSESSMENT
${assessment || 'N/A'}

PLAN
${plan || 'N/A'}`;

    // Add AI Clinical Support if available
    if (soapNote.ai_support_generated && soapNote.clinical_support) {
      const cs = soapNote.clinical_support;
      soapText += '\n\n--- AI CLINICAL DECISION SUPPORT ---\n';

      // Clinical Recommendations
      if (cs.clinical_recommendations && cs.clinical_recommendations.length > 0) {
        soapText += '\nCLINICAL RECOMMENDATIONS:\n';
        cs.clinical_recommendations.forEach((rec, idx) => {
          soapText += `${idx + 1}. ${rec}\n`;
        });
      }

      // Billing Codes
      if (cs.billing_codes && cs.billing_codes.length > 0) {
        soapText += '\nCDA BILLING CODES:\n';
        cs.billing_codes.forEach((code) => {
          soapText += `${code.step}. ${code.description} - ${code.code} (${code.timing})\n`;
        });
      }

      // Patient Education
      if (cs.patient_education && cs.patient_education.length > 0) {
        soapText += '\nPATIENT EDUCATION:\n';
        cs.patient_education.forEach((edu, idx) => {
          soapText += `${idx + 1}. ${edu}\n`;
        });
      }

      // Follow-up Recommendations
      if (cs.follow_up && cs.follow_up.length > 0) {
        soapText += '\nFOLLOW-UP RECOMMENDATIONS:\n';
        cs.follow_up.forEach((fu, idx) => {
          soapText += `${idx + 1}. ${fu}\n`;
        });
      }

      // Risk Assessment
      if (cs.risk_assessment && cs.risk_assessment.length > 0) {
        soapText += '\nRISK ASSESSMENT:\n';
        cs.risk_assessment.forEach((risk, idx) => {
          soapText += `${idx + 1}. ${risk}\n`;
        });
      }
    }

    navigator.clipboard.writeText(soapText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  const handleEditSOAP = () => {
    setIsEditing(true);
    setEditedSoapNote({ ...soapNote.soap_note });
  };

  const handleSaveEdit = async () => {
    console.log('[handleSaveEdit] Starting save edit process');
    console.log('[handleSaveEdit] Current soapNote.ai_support_generated:', soapNote.ai_support_generated);

    // Check if any changes were made
    const noChanges =
      editedSoapNote.subjective === soapNote.soap_note.subjective &&
      editedSoapNote.objective === soapNote.soap_note.objective &&
      editedSoapNote.assessment === soapNote.soap_note.assessment &&
      editedSoapNote.plan === soapNote.soap_note.plan;

    if (noChanges) {
      setErrorMessage('No changes were made to the SOAP note. Please edit at least one section before saving.');
      setShowErrorModal(true);
      return;
    }

    // Store the ai_support_generated flag before updating state
    const hadAiSupport = soapNote.ai_support_generated;

    // Prepare the updated soap note (but don't set it yet)
    const updatedSoapNote = {
      ...soapNote,
      soap_note: editedSoapNote
    };

    // Regenerate AI Clinical Support based on edited SOAP note
    console.log('[handleSaveEdit] hadAiSupport value:', hadAiSupport);
    if (hadAiSupport) {
      console.log('[handleSaveEdit] AI support was generated, regenerating...');
      console.log('[handleSaveEdit] Edited SOAP note being sent:', editedSoapNote);
      setIsRegeneratingSupport(true);

      try {
        // Call AI Clinical Support API with edited SOAP note (SOAP only, no transcript)
        console.log('[handleSaveEdit] Calling API with SOAP note:', editedSoapNote);

        const data = await soapApi.generateClinicalSupport(editedSoapNote);
        console.log('[handleSaveEdit] API Response data:', data);

        // Check if API returned an error (even with success: true)
        if (data.error || data.insufficient_content) {
          console.error('[handleSaveEdit] API returned error:', data.error || data.message);
          // Show error to user
          setErrorMessage(data.message || 'SOAP note does not contain sufficient information for clinical support generation. Please add more details to the SOAP note sections.');
          setShowErrorModal(true);
          // Don't update the SOAP note - keep user in editing mode
          // editedSoapNote remains set so user can continue editing
        } else if (data.success && data.clinical_support) {
          console.log('[handleSaveEdit] New clinical support received:', data.clinical_support);
          console.log('[handleSaveEdit] New billing codes:', data.clinical_support.billing_codes);
          console.log('[handleSaveEdit] New clinical recommendations:', data.clinical_support.clinical_recommendations);
          console.log('[handleSaveEdit] New patient education:', data.clinical_support.patient_education);
          console.log('[handleSaveEdit] New follow-up:', data.clinical_support.follow_up);
          console.log('[handleSaveEdit] New risk assessment:', data.clinical_support.risk_assessment);

          // Update SOAP note with new clinical support
          const updatedWithSupport = {
            ...updatedSoapNote,
            clinical_support: data.clinical_support,
            ai_support_generated: true
          };
          console.log('[handleSaveEdit] Updated SOAP note object:', {
            has_clinical_support: !!updatedWithSupport.clinical_support,
            billing_codes_count: updatedWithSupport.clinical_support?.billing_codes?.length,
            clinical_recs_count: updatedWithSupport.clinical_support?.clinical_recommendations?.length
          });
          console.log('[handleSaveEdit] Setting updated SOAP note with new clinical support');

          setSoapNote(updatedWithSupport);
          setIsEditing(false);
          setEditedSoapNote(null);  // Clear edited note after successful save
          setHasUnsavedChanges(true);  // Mark as having unsaved changes
          // Don't auto-save - user must click "Save to History" button
        } else {
          console.warn('[handleSaveEdit] Unexpected API response format:', data);
          // Don't update the SOAP note - keep user in editing mode
        }
      } catch (err) {
        console.error('Error regenerating clinical support:', err);
        // Show error to user
        setErrorMessage(`Failed to regenerate AI clinical support: ${err.message}. The original clinical support has been retained.`);
        setShowErrorModal(true);
        // Don't update the SOAP note - keep user in editing mode so they can fix the issue
      } finally {
        setIsRegeneratingSupport(false);
      }
    } else {
      // No AI support to regenerate, just apply the edit
      setSoapNote(updatedSoapNote);
      setIsEditing(false);
      setEditedSoapNote(null);
      setHasUnsavedChanges(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedSoapNote(null);
  };

  const handleEditField = (field, value) => {
    setEditedSoapNote({
      ...editedSoapNote,
      [field]: value
    });
    // Mark as having unsaved changes when user edits
    setHasUnsavedChanges(true);
  };

  const handleCloseSoapNote = () => {
    // Check if there are unsaved changes
    if (!isSaved || hasUnsavedChanges) {
      setShowCloseConfirmModal(true);
    } else {
      confirmCloseSoapNote();
    }
  };

  const confirmCloseSoapNote = () => {
    setSoapNote(null);
    setEditedSoapNote(null);
    setIsEditing(false);
    setIsSaved(false);
    setHasUnsavedChanges(false);
    setCurrentNoteId(null);
    setSelectedFile(null);
    setRecordedAudio(null);
    setRecordingDuration(0);
    setError(null);
    setShowCloseConfirmModal(false);
  };

  // Billing code editing functions
  const startEditingCode = (index, code) => {
    setEditingCodeIndex(index);
    setEditedCodeValue(code.code);
    setEditedCodeDescription(code.description);
    setEditedCodeTiming(code.timing || 'Today');
    setIsNewCode(false); // Editing existing code
  };

  const cancelEditingCode = () => {
    // If canceling a new code, remove it from the list
    if (isNewCode && editingCodeIndex !== null) {
      const updatedSoapNote = { ...soapNote };
      updatedSoapNote.clinical_support.billing_codes.splice(editingCodeIndex, 1);
      setSoapNote(updatedSoapNote);
    }

    setEditingCodeIndex(null);
    setEditedCodeValue('');
    setEditedCodeDescription('');
    setEditedCodeTiming('');
    setIsNewCode(false);
  };

  const saveEditedCode = (index) => {
    // Don't save if both fields are empty
    if (!editedCodeValue.trim() && !editedCodeDescription.trim()) {
      cancelEditingCode();
      return;
    }

    const updatedSoapNote = { ...soapNote };
    updatedSoapNote.clinical_support.billing_codes[index] = {
      ...updatedSoapNote.clinical_support.billing_codes[index],
      code: editedCodeValue,
      description: editedCodeDescription,
      timing: editedCodeTiming
    };
    setSoapNote(updatedSoapNote);
    setHasUnsavedChanges(true);
    setEditingCodeIndex(null);
    setEditedCodeValue('');
    setEditedCodeDescription('');
    setEditedCodeTiming('');
    setIsNewCode(false);
  };

  const deleteCode = (index) => {
    const updatedSoapNote = { ...soapNote };
    updatedSoapNote.clinical_support.billing_codes.splice(index, 1);
    setSoapNote(updatedSoapNote);
    setHasUnsavedChanges(true);
  };

  const addNewCode = () => {
    const updatedSoapNote = { ...soapNote };
    if (!updatedSoapNote.clinical_support.billing_codes) {
      updatedSoapNote.clinical_support.billing_codes = [];
    }
    updatedSoapNote.clinical_support.billing_codes.push({
      code: '',
      description: '',
      timing: 'Today'
    });
    setSoapNote(updatedSoapNote);
    setEditingCodeIndex(updatedSoapNote.clinical_support.billing_codes.length - 1);
    setEditedCodeValue('');
    setEditedCodeDescription('');
    setEditedCodeTiming('Today');
    setIsNewCode(true); // Mark as new code
    setHasUnsavedChanges(true);
  };

  const saveToHistory = async () => {
    setIsSavingHistory(true);
    try {
      let audioBase64 = null;
      if (recordedAudio && recordedAudio.blob) {
        audioBase64 = await blobToBase64(recordedAudio.blob);
      }

      // Build complete SOAP note data including clinical support
      const completeSoapNote = {
        ...soapNote,
        soap_note: editedSoapNote || soapNote.soap_note
      };

      // Prepare data for backend API
      const requestData = {
        patient_name: patientName,
        patient_id: selectedPatient?.id || null,  // Link to patient record
        appointment_date: appointmentDate,
        transcript: completeSoapNote.transcript || '',
        soap_note: completeSoapNote.soap_note,
        clinical_support: completeSoapNote.clinical_support,
        audio_base64: audioBase64,
        audio_duration: recordedAudio?.duration || completeSoapNote.duration || 0,
        file_name: selectedFile?.name || 'recording.webm',
        language: completeSoapNote.language || 'en',
        pii_redacted: completeSoapNote.pii_redacted || false,
        pii_found: completeSoapNote.pii_found || []
      };

      // DEBUG: Log what we're sending
      console.log('DEBUG [saveToHistory]: Saving with patient_id:', requestData.patient_id);

      // If we already have a saved note (currentNoteId exists), update it instead of creating new
      if (currentNoteId) {
        // Update existing note via API
        await soapApi.update(currentNoteId, requestData);
        setSuccessMessage('SOAP note updated in history!');
        setShowSuccessModal(true);
      } else {
        // Create new note via API
        const data = await soapApi.save(requestData);
        setCurrentNoteId(data.note_id);
        setShowSaveSuccessModal(true);
      }

      // Mark as saved and no unsaved changes
      setIsSaved(true);
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Failed to save to history:', err);
      setErrorMessage('Failed to save note to history');
      setShowErrorModal(true);
    } finally {
      setIsSavingHistory(false);
    }
  };

  const loadHistory = async () => {
    try {
      const data = await soapApi.getHistory();

      // Transform backend data format to frontend format
      const transformedNotes = data.notes.map(note => {
        return {
          id: note.id,
          soapNote: {
            soap_note: note.soap_note,  // Backend sends 'soap_note', not 'soap_content'
            transcript: note.transcript,
            clinical_support: note.clinical_support,
            duration: note.audio_duration,
            language: note.language,
            pii_redacted: note.pii_redacted,
            pii_found: note.pii_found || []
          },
          patientName: note.patient_name,
          appointmentDate: note.appointment_date,
          audioBase64: note.audio_base64,
          audioDuration: note.audio_duration,
          fileName: note.file_name,
          createdAt: note.created_at,
          updatedAt: note.updated_at
        };
      });

      setSoapHistory(transformedNotes);
      setShowHistory(true);
    } catch (err) {
      console.error('Failed to load history:', err);
      setErrorMessage('Failed to load history');
      setShowErrorModal(true);
    }
  };

  const getFilteredHistory = () => {
    return soapHistory.filter(note => {
      // Filter by patient name
      if (filterName && note.patientName) {
        const nameMatch = note.patientName.toLowerCase().includes(filterName.toLowerCase());
        if (!nameMatch) return false;
      }

      // Filter by date range
      if (filterDateFrom || filterDateTo) {
        if (!note.appointmentDate) return false;

        const noteDate = new Date(note.appointmentDate);

        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom);
          if (noteDate < fromDate) return false;
        }

        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          // Set to end of day for the "to" date
          toDate.setHours(23, 59, 59, 999);
          if (noteDate > toDate) return false;
        }
      }

      return true;
    });
  };

  const clearFilters = () => {
    setFilterName('');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  const handleLoadNote = async (note) => {
    setIsLoadingHistoryNote(true);
    console.log('DEBUG history click: attempting to load note', note?.id);
    try {
      // Set flag to prevent useEffect from clearing the note when we update appointmentDate
      isLoadingFromHistoryRef.current = true;

      const baseSoap = note.soapNote || note.soap_note || {};
      // Deep clone to force state change even if references match previous note
      const clonedSoap = JSON.parse(JSON.stringify(baseSoap || {}));

      // Add flags when loading from history so all sections display
      const loadedSoapNote = {
        ...clonedSoap,
        soap_generated: true,  // Mark as generated so SOAP sections display
        ai_support_generated: true  // Mark AI support as generated so clinical support displays
      };

      setSoapNote(loadedSoapNote);
      setPatientName(note.patientName || '');
      setAppointmentDate(note.appointmentDate || '');
      setCurrentNoteId(note.id);

      // Mark as saved since we're loading from history
      setIsSaved(true);
      setHasUnsavedChanges(false);

      // Reset edit state
      setIsEditing(false);
      setEditedSoapNote(null);

      // Reconstruct audio if available
      if (note.audioBase64) {
        try {
          const blob = base64ToBlob(note.audioBase64);
          const audioUrl = URL.createObjectURL(blob);
          setRecordedAudio({
            url: audioUrl,
            blob: blob,
            duration: note.audioDuration || 0
          });
        } catch (audioErr) {
          console.error('Failed to reconstruct audio from history note', audioErr);
          setRecordedAudio(null);
        }
      } else {
        // Clear audio if not available
        setRecordedAudio(null);
      }

      // Close the history drawer after state is set
      setShowHistory(false);
      console.log('DEBUG history click: note set into state', loadedSoapNote);
    } catch (err) {
      console.error('Failed to load history note:', err);
      setErrorMessage('Failed to load note from history. Please try again.');
      setShowErrorModal(true);
    } finally {
      setIsLoadingHistoryNote(false);
    }
  };

  const handleDeleteNote = (note) => {
    setNoteToDelete(note);
    setShowDeleteNoteModal(true);
  };

  const confirmDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      await soapApi.delete(noteToDelete.id);

      // Reload history after deletion
      await loadHistory();

      // Close modal and reset
      setShowDeleteNoteModal(false);
      setNoteToDelete(null);
    } catch (err) {
      console.error('Failed to delete note:', err);
      setErrorMessage('Failed to delete note');
      setShowErrorModal(true);
    }
  };

  const cancelDeleteNote = () => {
    setShowDeleteNoteModal(false);
    setNoteToDelete(null);
  };

  const handleGenerateSOAP = async () => {
    // Require patient info before generating
    if (!patientName || patientName.trim() === '') {
      setError('Please enter the patient name before generating a SOAP note.');
      return;
    }
    if (!appointmentDate || !/^\d{4}-\d{2}-\d{2}$/.test(appointmentDate)) {
      setError('Please select a valid appointment date (YYYY-MM-DD).');
      return;
    }
    if (!recordedAudio && !selectedFile) {
      setError('No audio file available');
      return;
    }

    // Proactively refresh the token if it has less than 10 minutes remaining.
    // This prevents a near-expired token from expiring mid-upload/processing.
    try {
      const accessToken = localStorage.getItem('accessToken');
      if (accessToken) {
        const payload = JSON.parse(atob(accessToken.split('.')[1]));
        const secondsRemaining = payload.exp - Math.floor(Date.now() / 1000);
        if (secondsRemaining < 600) {
          console.log(`[Generate] Token has ${secondsRemaining}s remaining — refreshing before generation`);
          await refreshAccessToken();
        }
      }
    } catch (e) {
      // Malformed token — let the request fail naturally and the 401 handler will catch it
    }

    setIsGenerating(true);
    setError(null);
    setSoapNote(null);
    setCurrentGeneratingStage('noise_removal'); // Start tracking from noise removal
    // Reset save state for new note generation
    setCurrentNoteId(null);
    setIsSaved(false);
    setHasUnsavedChanges(false);

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      const formData = new FormData();

      if (recordedAudio) {
        // Use recorded audio
        formData.append('file', recordedAudio.blob, 'recording.webm');
      } else if (selectedFile) {
        // Use uploaded file
        formData.append('file', selectedFile);
      }

      // Simulate stage progression based on typical processing times
      // Update stages as the request progresses
      const stageProgression = [
        { stage: 'noise_removal', delay: 30 },      // 30s
        { stage: 'transcribing', delay: 65 },       // +35s
        { stage: 'pii_redaction', delay: 80 },      // +15s
        { stage: 'soap_generation', delay: 125 },   // +45s
        { stage: 'ai_analysis', delay: 150 }        // +25s
      ];

      // Set up stage transitions
      stageTimersRef.current = stageProgression.map((item, index) => {
        return setTimeout(() => {
          if (index < stageProgression.length) {
            setCurrentGeneratingStage(item.stage);
          }
        }, item.delay * 1000);
      });

      const data = await soapApi.generateFromAudio(formData, abortControllerRef.current.signal);

      // Clear all timers on completion
      stageTimersRef.current.forEach(timer => clearTimeout(timer));
      stageTimersRef.current = [];

      // DEBUG: Log the response to see what we're getting
      console.log('=== BACKEND RESPONSE ===');
      console.log('Full response:', data);
      console.log('soap_generated:', data.soap_generated);
      console.log('soap_note:', data.soap_note);
      console.log('transcript:', data.transcript?.substring(0, 100) + '...');
      console.log('=======================');

      // Check if the backend returned insufficient content flag
      if (data.insufficient_content) {
        setError('The audio does not contain sufficient dental clinical information. Please record or upload an audio file of an actual dental appointment or procedure.');
        setSoapNote(null);
      } else {
        setSoapNote(data);
      }

    } catch (err) {
      // Check if error is due to abort (user cancelled the transcription)
      if (err.name === 'AbortError') {
        console.log('Transcription aborted by user');
        setError(null);
        abortControllerRef.current = null;
      } else {
        console.error('Error generating SOAP note:', err);
        setError(err.message || 'Failed to generate SOAP note. Please try again.');
      }
    } finally {
      setIsGenerating(false);
      setCurrentGeneratingStage(null); // Clear the stage when done
    }
  };

  // Token refresh logic
  const scheduleTokenRefresh = (expiresInSeconds) => {
    // Clear any existing refresh timer
    if (window.tokenRefreshTimer) {
      clearTimeout(window.tokenRefreshTimer);
    }

    // Schedule refresh before token expires (2 minutes before expiration)
    window.tokenRefreshTimer = setTimeout(async () => {
      await refreshAccessToken();
    }, expiresInSeconds * 1000);
  };

  const refreshAccessToken = async (retryCount = 0) => {
    const maxRetries = 3; // Maximum number of retry attempts

    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        handleLogout();
        return;
      }

      const data = await authApi.refreshToken(refreshToken);

      if (data.success) {
        // Update access token
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('lastActivity', Date.now().toString()); // Update activity on token refresh
        // Schedule next refresh (2 minutes before expiration)
        scheduleTokenRefresh(data.expiresIn - 120);
        console.log("Token refreshed successfully");

        return true;
      } else {
        // Refresh failed, logout user
        throw new Error(data.error || 'Token refresh failed');
      }
    } catch (err) {
      console.error(`Token refresh attempt ${retryCount + 1}: failed`, err.message);

      if (retryCount < maxRetries - 1) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        console.log(`Retrying token refresh in ${delay}ms...`);

        await new Promise(resolve => setTimeout(resolve, delay));
        return await refreshAccessToken(retryCount + 1);
      }
      console.log("All token refresh attempts failed, logging out");
      setShowSessionExpiredModal(true);

      handleLogout();
      return false;
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setAccessError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setAccessError('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 5) {
      setAccessError('Password must be at least 5 characters long');
      return;
    }

    // Validate password has at least one capital letter
    if (!/[A-Z]/.test(password)) {
      setAccessError('Password must contain at least one capital letter');
      return;
    }

    // Validate password has at least one number
    if (!/[0-9]/.test(password)) {
      setAccessError('Password must contain at least one number');
      return;
    }

    // Validate full name
    if (fullName.trim().length < 2) {
      setAccessError('Please enter your full name');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setAccessError('Please enter a valid email address');
      return;
    }

    try {
      const data = await authApi.signup(email, password, confirmPassword, fullName, loginMode);

      if (data.success) {
        // Show success message
        setAccessError('');
        setShowSignupSuccessModal(true);

        // Switch to login mode
        setShowSignup(false);
        setPassword('');
        setConfirmPassword('');
        setFullName('');
      } else {
        setAccessError(data.error || 'Signup failed');
      }
    } catch (err) {
      console.error('Signup error:', err);
      setAccessError('Unable to connect to server. Please try again.');
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setAccessError('');

    // Handle different login modes
    if (loginMode === 'doctor' || loginMode === 'patient') {
      try {
        // Call backend user login API
        const data = await authApi.login(email, password, loginMode);

        if (data.success) {
          // Store JWT tokens securely (ONLY tokens, not user data)
          localStorage.setItem('accessToken', data.accessToken);
          localStorage.setItem('refreshToken', data.refreshToken);
          localStorage.setItem('loginMode', loginMode);
          localStorage.setItem('lastActivity', Date.now().toString()); // Initialize activity tracking

          // HIPAA/PIPEDA compliant: Store user data in state, verified by server
          setCurrentUser(data.user);
          setIsFirstLogin(data.isFirstLogin === true);

          // Schedule token refresh before expiration (13 minutes, tokens expire in 15)
          scheduleTokenRefresh(data.expiresIn - 120);

          setIsAuthenticated(true);
          setAccessError('');
          setShowSignupSuccessModal(false); // Close signup success modal if it was open
        } else {
          // Special handling for unverified email
          if (data.error_code === 'EMAIL_NOT_VERIFIED') {
            setAccessError('Please verify your email address before logging in. Check your inbox for the verification link we sent you.');
          }
          // Show remaining attempts if available
          else if (data.remaining_attempts !== undefined) {
            setAccessError(`${data.error}. ${data.remaining_attempts} attempt(s) remaining.`);
          }
          // Show lockout time if rate limited
          else if (data.lockout_time) {
            setAccessError(`Too many attempts. Please try again in ${Math.ceil(data.lockout_time / 60)} minute(s).`);
          }
          // Generic error
          else {
            setAccessError(data.error || 'Authentication failed');
          }
        }
      } catch (err) {
        console.error('User authentication error:', err);
        setAccessError('Unable to connect to server. Please try again.');
      }
    }
    return;
  };

  const handleClaimAccount = async () => {
    try {
      setClaimingAccount(true);

      const data = await patientApi.claimAccount();

      if (data.success) {
        setAccountClaimed(true);
        setSuccessMessage(data.message || 'Account claimed successfully!');
        setShowSuccessModal(true);

        // Refresh patient notes after claiming
        const notesData = await patientApi.getMyNotes();
        if (notesData.success) {
          setPatientNotes(notesData.notes || []);
        }
      } else {
        setErrorMessage(data.error || 'Failed to claim account. Please contact your healthcare provider.');
        setShowErrorModal(true);
      }
    } catch (err) {
      console.error('Error claiming account:', err);
      setErrorMessage('Unable to claim account. Please try again.');
      setShowErrorModal(true);
    } finally {
      setClaimingAccount(false);
    }
  };

  const handleCreatePatient = async () => {
    try {
      // Validate that patient email is not the same as doctor's email
      if (newPatientData.email && currentUser?.email &&
          newPatientData.email.toLowerCase().trim() === currentUser.email.toLowerCase().trim()) {
        setPatientModalError('Cannot create a patient with the same email as your account. Please use a different email.');
        return;
      }

      const data = await patientApi.create(newPatientData);

      if (data.success) {
        // Add new patient to list
        setPatients([data.patient, ...patients]);

        // Select the newly created patient
        setSelectedPatient(data.patient);
        setPatientName(data.patient.full_name);

        // Close modal and reset form
        setShowPatientModal(false);
        setNewPatientData({ fullName: '', email: '', dateOfBirth: '', phone: '' });

        setPatientModalError('');
      } else {
        setPatientModalError(data.error || 'Failed to create patient');
      }
    } catch (err) {
      console.error('Error creating patient:', err);
      setPatientModalError('Unable to create patient. Please try again.');
    }
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setPatientName(patient.full_name);
  };

  const handleEditPatient = (patient) => {
    // Open edit modal with current patient data
    setPatientToEdit(patient);
    setEditPatientData({
      email: patient.email || '',
      phone: patient.phone || ''
    });
    setEditPatientError('');
    setShowEditPatientModal(true);
  };

  const confirmEditPatient = async () => {
    if (!patientToEdit) return;

    // Validate phone is provided
    if (!editPatientData.phone) {
      setEditPatientError('Please provide a phone number');
      return;
    }

    try {
      const data = await patientApi.update(patientToEdit.id, {
        phone: editPatientData.phone || undefined,
      });

      if (data.success) {
        // Update patient in list
        setPatients(patients.map(p =>
          p.id === patientToEdit.id ? data.patient : p
        ));

        // If this was the selected patient, update selection
        if (selectedPatient?.id === patientToEdit.id) {
          setSelectedPatient(data.patient);
          setPatientName(data.patient.full_name);
        }

        setEditPatientError('');
        // Close modal and reset
        setShowEditPatientModal(false);
        setPatientToEdit(null);
        setEditPatientData({ phone: '' });
      } else {
        setEditPatientError(data.error || 'Failed to update patient');
      }
    } catch (err) {
      console.error('Error updating patient:', err);
      setEditPatientError('Unable to update patient. Please try again.');
    }
  };

  const cancelEditPatient = () => {
    setShowEditPatientModal(false);
    setPatientToEdit(null);
    setEditPatientData({ phone: '' });
    setEditPatientError('');
  };

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');

      // Call backend logout endpoint if refresh token exists
      if (refreshToken) {
        await authApi.logout(refreshToken);
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear refresh timer
      if (window.tokenRefreshTimer) {
        clearTimeout(window.tokenRefreshTimer);
        window.tokenRefreshTimer = null;
      }

      // SECURITY: Clear ALL state to prevent data leakage between sessions
      setIsAuthenticated(false);
      setCurrentUser(null); // HIPAA/PIPEDA compliant: Clear server-verified user data
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setFullName('');
      setAccessError('');
      setLoginMode('doctor');
      setShowSignup(false);

      // Clear SOAP note state
      setSoapNote(null);
      setRecordedAudio(null);
      setSelectedFile(null);
      setPatientName('');
      setSelectedPatient(null);
      setAppointmentDate('');
      setIsRecording(false);
      setRecordingDuration(0);
      setIsGenerating(false);
      setError(null);
      setCopied(false);
      setIsEditing(false);
      setEditedSoapNote(null);
      setIsRegeneratingSupport(false);
      setIsSaved(false);
      setHasUnsavedChanges(false);

      // Clear history state
      setShowHistory(false);
      setSoapHistory([]);
      setCurrentNoteId(null);
      setShowFilters(false);
      setFilterName('');
      setFilterDateFrom('');
      setFilterDateTo('');

      // Clear patient management state
      setPatients([]);
      setShowPatientModal(false);
      setNewPatientData({ fullName: '', email: '', dateOfBirth: '', phone: '' });

      // Clear patient portal state
      setPatientNotes([]);
      setLoadingNotes(false);
      setAccountClaimed(false);
      setClaimingAccount(false);

      // Remove all tokens from localStorage (no user data stored here)
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('authToken');
      localStorage.removeItem('loginMode');
      localStorage.removeItem('lastActivity'); // Clear inactivity tracking

      console.log('SECURITY: All state cleared on logout');
    }
  };

  const handleChangePassword = () => {
    if (!currentUser?.email) {
      setNotificationModal({
        show: true,
        type: 'error',
        title: 'Authentication Required',
        message: 'Unable to send password change email. Please try logging in again.'
      });
      return;
    }
    // Show confirmation modal before sending email
    setShowPasswordChangeModal(true);
  };

  const confirmChangePassword = async () => {
    try {
      const response = await fetch(apiUrl('/api/auth/change-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: currentUser.email }),
      });

      const data = await response.json();

      if (data.success) {
        setShowPasswordChangeModal(false);
        setNotificationModal({
          show: true,
          type: 'success',
          title: 'Email Sent',
          message: 'Password change email sent successfully. Please check your inbox.'
        });
      } else {
        setShowPasswordChangeModal(false);
        setNotificationModal({
          show: true,
          type: 'error',
          title: 'Failed',
          message: 'Failed to send password change email. Please try again later.'
        });
      }
    } catch (err) {
      console.error('Change password error:', err);
      setShowPasswordChangeModal(false);
      setNotificationModal({
        show: true,
        type: 'error',
        title: 'Error',
        message: 'An error occurred while sending the password change email. Please try again later.'
      });
    }
  };

  // Page navigation - show Privacy Policy or Terms
  if (currentPage === 'privacy') {
    return <PrivacyPolicy onBack={() => setCurrentPage('main')} />;
  }

  if (currentPage === 'terms') {
    return <TermsConditions onBack={() => setCurrentPage('main')} />;
  }

  if (currentPage === 'profile') {
    return (
      <ProfilePage
        currentUser={currentUser}
        onBack={() => setCurrentPage('main')}
        onProfileUpdate={(updated) => {
          setCurrentUser((prev) => ({ ...prev, ...updated }));
        }}
      />
    );
  }

  // Show auth screen if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <AuthScreen
          showSignup={showSignup}
          setShowSignup={setShowSignup}
          loginMode={loginMode}
          setLoginMode={setLoginMode}
          email={email}
          setEmail={setEmail}
          password={password}
          setPassword={setPassword}
          confirmPassword={confirmPassword}
          setConfirmPassword={setConfirmPassword}
          fullName={fullName}
          setFullName={setFullName}
          showPassword={showPassword}
          setShowPassword={setShowPassword}
          accessError={accessError}
          setAccessError={setAccessError}
          handleSignup={handleSignup}
          handleAuth={handleAuth}
          handleDemoCtaClick={handleDemoCtaClick}
          setCurrentPage={setCurrentPage}
          setCurrentUser={setCurrentUser}
          setIsAuthenticated={setIsAuthenticated}
          setIsFirstLogin={setIsFirstLogin}
          showSignupSuccessModal={showSignupSuccessModal}
          setShowSignupSuccessModal={setShowSignupSuccessModal}
        />
        {showSessionExpiredModal && (
          <SessionExpiredModal
            onLogin={() => {
              setShowSessionExpiredModal(false);
              handleLogout();
            }}
          />
        )}
      </>
    );
  }

  // HIPAA/PIPEDA Compliant: Get user role from server-verified state
  // Never trust client-side storage for role-based access control
  const userRole = currentUser?.role;
  const userEmail = currentUser?.email;

  // Patient Portal View
  if (userRole === 'patient') {
    return (
      <PatientDashboard
        currentUser={currentUser}
        patientNotes={patientNotes}
        loadingNotes={loadingNotes}
        accountClaimed={accountClaimed}
        claimingAccount={claimingAccount}
        handleClaimAccount={handleClaimAccount}
        patientFilterDateFrom={patientFilterDateFrom}
        setPatientFilterDateFrom={setPatientFilterDateFrom}
        patientFilterDateTo={patientFilterDateTo}
        setPatientFilterDateTo={setPatientFilterDateTo}
        showSuccessModal={showSuccessModal}
        setShowSuccessModal={setShowSuccessModal}
        successMessage={successMessage}
        showErrorModal={showErrorModal}
        setShowErrorModal={setShowErrorModal}
        errorMessage={errorMessage}
        handleLogout={handleLogout}
      />
    );
  }

  // Doctor/Admin View (Default)
  return (
    <div className="App">
      <DoctorOnboarding isFirstLogin={isFirstLogin} />
      <div className="container app-page-container">
        <div className="header-section">
          <div className="header-top">
            <div className="logo-wrapper">
              <div className="logo-icon">
                <svg viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Algodentra logo">
                  <g transform="translate(8.5 0)">
                    <path d="M10 30 L20 10 L30 30 Z" stroke="#ffffff" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="38" y1="20" x2="38" y2="20" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="43" y1="15" x2="43" y2="25" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="48" y1="10" x2="48" y2="30" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="53" y1="12" x2="53" y2="28" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="58" y1="18" x2="58" y2="22" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="63" y1="14" x2="63" y2="26" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="68" y1="16" x2="68" y2="24" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="73" y1="20" x2="73" y2="20" stroke="#ffffff" strokeWidth="3" strokeLinecap="round"/>
                  </g>
                </svg>
              </div>
              <div className="mobile-lp-logo">
                <svg width="120" height="60" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Algodentra logo">
                  <g>
                    <path d="M10 30 L20 10 L30 30 Z" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="38" y1="20" x2="38" y2="20" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="43" y1="15" x2="43" y2="25" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="48" y1="10" x2="48" y2="30" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="53" y1="12" x2="53" y2="28" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="58" y1="18" x2="58" y2="22" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="63" y1="14" x2="63" y2="26" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="68" y1="16" x2="68" y2="24" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                    <line x1="73" y1="20" x2="73" y2="20" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  </g>
                </svg>
              </div>
              <h1 className="title">Algodentra</h1>
            </div>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              {/* Subscription Status Badge or Trial CTA Button */}
              {subscriptionStatus?.has_subscription ? (
                isSubscriptionEnded ? (
                  <button
                    className="trial-cta-button trial-cta-button-upgrade"
                    onClick={() => setShowPricingModal(true)}
                  >
                    Upgrade Now
                  </button>
                ) : (
                  <div className="subscription-badge">
                    <div className="subscription-badge-icon">
                      {isSubscriptionInTrial ? '🎉' : '✓'}
                    </div>
                    <div className="subscription-badge-text">
                      <div className="subscription-badge-plan">
                        {subscriptionStatus.subscription.plan_display_name} Plan
                      </div>
                      {isSubscriptionInTrial && (
                        <div className="subscription-badge-trial">
                          Free Trial Active
                        </div>
                      )}
                    </div>
                  </div>
                )
              ) : (
                <button
                  className="trial-cta-button"
                  onClick={() => setShowPricingModal(true)}
                >
                  Start 14 day free trial
                </button>
              )}

              {/* Profile Menu */}
              <div className="profile-menu-container">
                <button
                  id="tour-profile-menu"
                  className="profile-menu-button"
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  aria-label="User profile menu"
                >
                  <div className="profile-avatar">
                    {currentUser?.full_name ?
                      currentUser.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) :
                      userEmail?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <span className="profile-greeting">
                    Welcome back {currentUser?.full_name ? (
                      currentUser.role === 'doctor'
                        ? `Dr. ${currentUser.full_name.split(' ').slice(-1)[0]}`
                        : currentUser.full_name.split(' ')[0]
                    ) : 'User'}
                  </span>
                  <svg
                    className={`profile-dropdown-icon ${showProfileDropdown ? 'open' : ''}`}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                  >
                    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>

                {showProfileDropdown && (
                  <>
                    <div className="profile-dropdown-overlay" onClick={() => setShowProfileDropdown(false)} />
                    <div className="profile-dropdown-menu">
                      <div className="profile-dropdown-header">
                        <div className="profile-dropdown-avatar">
                          {currentUser?.full_name ?
                            currentUser.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) :
                            userEmail?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="profile-dropdown-info">
                          <div className="profile-dropdown-name">{currentUser?.full_name || 'User'}</div>
                      <div className="profile-dropdown-email">{userEmail}</div>
                    </div>
                  </div>
                  <div className="profile-dropdown-divider" />
                  {subscriptionStatus?.has_subscription ? (
                    isSubscriptionEnded ? (
                      <button className="profile-dropdown-item profile-dropdown-item-trial profile-dropdown-item-upgrade" onClick={() => {
                        setShowProfileDropdown(false);
                        setShowPricingModal(true);
                      }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 12V4M8 4L5 7M8 4l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Upgrade Now
                        </button>
                      ) : (
                        <div className="profile-dropdown-subscription">
                          <div className="profile-dropdown-subscription-header">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M8 1l2 4 4.5.5-3.25 3.25L12 14l-4-2.5L4 14l.75-5.25L1.5 5.5 6 5l2-4z" fill="currentColor"/>
                            </svg>
                            <span>{subscriptionStatus.subscription.plan_display_name} Plan</span>
                          </div>
                          {isSubscriptionInTrial && (
                            <div className="profile-dropdown-subscription-trial">
                              Free Trial Active
                            </div>
                          )}
                          {subscriptionStatus.subscription.trial_end && isSubscriptionInTrial && (
                            <div className="profile-dropdown-subscription-date">
                              Ends {new Date(subscriptionStatus.subscription.trial_end).toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </div>
                          )}
                          {subscriptionStatus.subscription.current_period_end && subscriptionStatus.subscription.status === 'active' && (
                            <div className="profile-dropdown-subscription-date">
                              Renews {new Date(subscriptionStatus.subscription.current_period_end).toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </div>
                          )}
                          {subscriptionStatus.usage && (
                            <div className="profile-dropdown-subscription-usage">
                              {subscriptionStatus.usage.unlimited ? (
                                'Unlimited SOAP notes'
                              ) : (
                                `${subscriptionStatus.usage.used_today}/${subscriptionStatus.usage.daily_limit} notes used today`
                              )}
                            </div>
                          )}
                          <button
                            className="profile-dropdown-item profile-dropdown-item-danger"
                            onClick={() => setShowCancelSubscriptionModal(true)}
                            disabled={isCancelingSubscription}
                            style={{ marginTop: '0.75rem' }}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M4 4l8 8M12 4L4 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            {isCancelingSubscription ? 'Canceling...' : 'Cancel subscription'}
                          </button>
                        </div>
                      )
                  ) : (
                    <button className="profile-dropdown-item profile-dropdown-item-trial" onClick={() => {
                      setShowProfileDropdown(false);
                          setShowPricingModal(true);
                        }}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 1l2 4 4.5.5-3.25 3.25L12 14l-4-2.5L4 14l.75-5.25L1.5 5.5 6 5l2-4z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Start 14 day free trial
                        </button>
                      )}
                      <button className="profile-dropdown-item" onClick={() => {
                        setShowProfileDropdown(false);
                        setCurrentPage('profile');
                      }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="5.5" r="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M2 14c0-3.31 2.69-5 6-5s6 1.69 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        Profile
                      </button>
                      <button id="tour-history" className="profile-dropdown-item" onClick={() => {
                        setShowProfileDropdown(false);
                        loadHistory();
                      }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M8 4v4l2.5 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                        History
                      </button>
                      <button className="profile-dropdown-item" onClick={() => {
                        setShowProfileDropdown(false);
                        handleChangePassword();
                      }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M12 7V5C12 2.79 10.21 1 8 1C5.79 1 4 2.79 4 5V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 12V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12.5 15H3.5C2.67 15 2 14.33 2 13.5V8.5C2 7.67 2.67 7 3.5 7H12.5C13.33 7 14 7.67 14 8.5V13.5C14 14.33 13.33 15 12.5 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Change Password
                      </button>
                      <button className="profile-dropdown-item profile-dropdown-item-danger" onClick={() => {
                        setShowProfileDropdown(false);
                        handleLogout();
                      }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M6 14H3.5C2.67 14 2 13.33 2 12.5V3.5C2 2.67 2.67 2 3.5 2H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M11 11L14 8L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M14 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <p className="subtitle">AI-Powered SOAP Note Generator</p>
          <div className="divider"></div>
        </div>

        <div className="main-content">
            <div className="portal-hero">
              <div className="portal-hero-left">
                <div className="portal-copy">
                  <p className="portal-eyebrow">Dentist Portal</p>
                  <h2 className="portal-title">Generate secure SOAP notes</h2>
                  <p className="portal-subtitle">
                    Search a patient, set the appointment, then record or upload to keep billing, history, and compliance aligned.
                  </p>
              </div>
            </div>
            <div className="portal-hero-right">
              <div className="portal-patient-chip">
                <span className="portal-chip-label">Active patient</span>
                <strong>{patientName || selectedPatient?.full_name || 'Not selected'}</strong>
              </div>
            </div>
          </div>

          {(!recordedAudio) || (soapNote && soapNote.soap_generated) ? (
            <MobileStartVisit
              patients={patients}
              patientSearchQuery={patientSearchQuery}
              setPatientSearchQuery={setPatientSearchQuery}
              showPatientDropdown={showPatientDropdown}
              setShowPatientDropdown={setShowPatientDropdown}
              selectedPatient={selectedPatient}
              handleSelectPatient={handleSelectPatient}
              setShowPatientModal={setShowPatientModal}
              onClearSelectedPatient={() => {
                setSelectedPatient(null);
                setPatientName('');
                setPatientSearchQuery('');
              }}
            appointmentDate={appointmentDate}
            setAppointmentDate={setAppointmentDate}
            appointmentDateError={appointmentDateError}
            setAppointmentDateError={setAppointmentDateError}
            onStartRecording={handleStartRecording}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            formatDuration={formatDuration}
            soapNote={soapNote}
            patientName={patientName}
            isEditing={isEditing}
            editedSoapNote={editedSoapNote}
            handleEditField={handleEditField}
            handleEditSOAP={handleEditSOAP}
            handleCancelEdit={handleCancelEdit}
            handleSaveEdit={handleSaveEdit}
            saveToHistory={saveToHistory}
            isSavingHistory={isSavingHistory}
            isSaved={isSaved}
            hasUnsavedChanges={hasUnsavedChanges}
            copied={copied}
            handleCopySOAP={handleCopySOAP}
            onBackToStart={confirmCloseSoapNote}
            editingCodeIndex={editingCodeIndex}
            editedCodeValue={editedCodeValue}
            setEditedCodeValue={setEditedCodeValue}
            editedCodeDescription={editedCodeDescription}
            setEditedCodeDescription={setEditedCodeDescription}
            editedCodeTiming={editedCodeTiming}
            setEditedCodeTiming={setEditedCodeTiming}
            startEditingCode={startEditingCode}
            saveEditedCode={saveEditedCode}
            cancelEditingCode={cancelEditingCode}
            addNewCode={addNewCode}
            deleteCode={deleteCode}
            currentUser={currentUser}
            selectedFile={selectedFile}
            handleFileUpload={handleFileUpload}
            fileInputRef={fileInputRef}
            handleGenerateSOAP={handleGenerateSOAP}
            isGenerating={isGenerating}
          />
          ) : null}

          {/* Patient Information Input */}
          {!soapNote && (
          <div className="patient-panel">
            <div className="patient-field">
              <label className="field-label" htmlFor="patientSelect">Select Patient</label>
              <select
                id="patientSelect"
                className="field-input"
                value={selectedPatient ? selectedPatient.id : ''}
                onChange={(e) => {
                  const patient = patients.find(p => p.id === parseInt(e.target.value));
                  if (patient) {
                    handleSelectPatient(patient);
                  } else {
                    setSelectedPatient(null);
                    setPatientName('');
                  }
                }}
              >
                <option value="">-- Choose a patient --</option>
                {patients.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.full_name}
                  </option>
                ))}
              </select>
              <div className="patient-actions">
                <button
                  id="tour-add-patient"
                  className="portal-ghost"
                  onClick={() => setShowPatientModal(true)}
                >
                  Add Patient
                </button>
                {selectedPatient && (
                  <button
                    className="portal-primary"
                    onClick={() => handleEditPatient(selectedPatient)}
                  >
                    Edit Patient
                  </button>
                )}
              </div>
              {selectedPatient && !soapNote && (
                <div className="patient-summary">
                  {selectedPatient.email && <div>Email: {selectedPatient.email}</div>}
                  {selectedPatient.phone && <div>Phone: {selectedPatient.phone}</div>}
                  <div className="patient-meta">Patient ID: {selectedPatient.id}</div>
                </div>
              )}
            </div>
            <div className="patient-field">
              <label className="field-label" htmlFor="appointmentDate">Appointment Date</label>
              <input
                id="appointmentDate"
                className="field-input"
                type="date"
                value={appointmentDate}
                max={(() => {
                  const today = new Date();
                  const year = today.getFullYear();
                  const month = String(today.getMonth() + 1).padStart(2, '0');
                  const day = String(today.getDate()).padStart(2, '0');
                  return `${year}-${month}-${day}`;
                })()}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const today = new Date();
                  const year = today.getFullYear();
                  const month = String(today.getMonth() + 1).padStart(2, '0');
                  const day = String(today.getDate()).padStart(2, '0');
                  const todayString = `${year}-${month}-${day}`;

                  if (selectedDate > todayString) {
                    setAppointmentDateError('Appointment date cannot be in the future.');
                    setAppointmentDate(selectedDate);
                  } else {
                    setAppointmentDateError('');
                    setAppointmentDate(selectedDate);
                  }
                }}
              />
              {appointmentDateError && (
                <div className="field-error">
                  <span>⚠️</span>
                  <span>{appointmentDateError}</span>
                </div>
              )}
            </div>
          </div>
          )}

          <div className="action-section">
            {!selectedFile && !soapNote && !recordedAudio && (
              <>
                <button
                  id="tour-record"
                  className={`action-btn primary-btn ${isRecording ? 'recording' : ''}`}
                  onClick={handleStartRecording}
                  disabled={soapNote !== null || recordedAudio !== null}
                >
                  <span className="btn-icon">
                    {isRecording ? (
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="6" y="6" width="12" height="12" fill="currentColor" rx="2"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="12" cy="12" r="4" fill="currentColor"/>
                      </svg>
                    )}
                  </span>
                  <span className="btn-text">
                    {isRecording ? 'Stop Recording' : 'Start Recording'}
                  </span>
                  {isRecording && <span className="recording-indicator"></span>}
                </button>

                <div className="divider-text">
                  <span>or</span>
                </div>
              </>
            )}

            {!soapNote && !recordedAudio && !selectedFile && (
              <div
                id="tour-attach-audio"
                className={`drag-drop-zone ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <label className="action-btn secondary-btn" htmlFor="file-upload">
                  <span className="btn-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span className="btn-text">Attach Audio File</span>
                  <input
                    id="file-upload"
                    type="file"
                    accept="audio/*"
                    onChange={handleFileUpload}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                  />
                </label>
                {isDragging && (
                  <div className="drag-overlay">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M17 8L12 3L7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p>Drop audio file here</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedFile && !soapNote && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              margin: '1rem 0'
            }}>
              {/* Audio player pill */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                padding: '1rem',
                backgroundColor: 'rgba(17, 94, 89, 0.2)',
                border: '2px solid rgba(17, 94, 89, 0.4)',
                borderRadius: '2rem',
                overflow: 'hidden'
              }}>
                {/* Hidden audio element */}
                <audio
                  ref={fileAudioRef}
                  onTimeUpdate={handleFileAudioTimeUpdate}
                  onEnded={handleFileAudioEnded}
                  onLoadedMetadata={handleFileAudioLoadedMetadata}
                >
                  <source src={selectedFile ? URL.createObjectURL(selectedFile) : ''} type={selectedFile?.type || 'audio/mpeg'} />
                </audio>

                {/* Play button */}
                <button
                  onClick={handlePlayFile}
                  style={{
                    flexShrink: 0,
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#10b981',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    padding: 0,
                    transition: 'background-color 0.2s'
                  }}
                  aria-label={isPlayingFile ? 'Pause audio' : 'Play audio'}
                >
                  {isPlayingFile ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px' }}>
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px' }}>
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  )}
                </button>

                {/* File name and duration */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, color: '#e5e7eb', fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    File uploaded: {selectedFile.name}
                  </p>
                </div>

                {/* Progress bar and duration */}
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <div
                    onClick={(e) => {
                      if (!fileAudioRef.current || !fileAudioDuration) return;
                      const rect = e.currentTarget.getBoundingClientRect();
                      const percent = (e.clientX - rect.left) / rect.width;
                      const newTime = percent * fileAudioDuration;
                      fileAudioRef.current.currentTime = newTime;
                      setFileAudioCurrentTime(newTime);
                    }}
                    style={{
                      flex: 1,
                      height: '4px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '2px',
                      overflow: 'hidden',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{
                      height: '100%',
                      backgroundColor: '#10b981',
                      width: `${fileAudioDuration > 0 ? (fileAudioCurrentTime / fileAudioDuration) * 100 : 0}%`,
                      borderRadius: '2px',
                      transition: 'width 0.1s linear'
                    }}></div>
                  </div>
                  <span style={{ color: '#9ca3af', fontSize: '0.75rem', whiteSpace: 'nowrap', fontWeight: 500 }}>
                    {formatAudioTime(fileAudioCurrentTime)} / {formatAudioTime(fileAudioDuration)}
                  </span>
                </div>

                {/* Remove button */}
                <button
                  onClick={handleRemoveFile}
                  style={{
                    flexShrink: 0,
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#ef4444',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    padding: 0,
                    transition: 'background-color 0.2s'
                  }}
                  aria-label="Remove attached file"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px' }}>
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/>
                  </svg>
                </button>
              </div>
            </div>
          )}

          {isRecording && (
            <div className="recording-timer">
              <div className="timer-content">
                <div className="pulse-dot"></div>
                <span>Recording: {formatDuration(recordingDuration)}</span>
              </div>
            </div>
          )}

          {recordedAudio && (
            <div className="audio-player-container">
              <div className="audio-player-header">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 18V5L21 12L9 18Z" fill="currentColor"/>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none"/>
                </svg>
                <div className="audio-info">
                  <h3>Recorded Audio</h3>
                  <p>Duration: {formatDuration(recordedAudio.duration)}</p>
                </div>
              </div>
              <audio controls className="audio-player">
                <source src={recordedAudio.url} type="audio/webm" />
                Your browser does not support the audio element.
              </audio>
            </div>
          )}

          {/* Progress Bar */}
          {isGenerating && <ProgressBar isGenerating={isGenerating} currentStage={currentGeneratingStage} />}

          {(recordedAudio || selectedFile) && !soapNote && (
            <div className="generate-section">
              {(!patientName || !appointmentDate) && (
                <div className="generate-warning">
                  Please add patient name and appointment date before generating a SOAP note.
                </div>
              )}
              <div className="generate-row">
                <button
                  className="generate-btn"
                  onClick={handleGenerateSOAP}
                  disabled={isGenerating || !patientName || !appointmentDate || appointmentDateError}
                >
                  <span className="btn-icon">
                    {isGenerating ? (
                      <svg className="spinner" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25"/>
                        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="currentColor"/>
                      </svg>
                    )}
                  </span>
                  <span className="btn-text">
                    {isGenerating ? 'Generating SOAP Note...' : 'Generate SOAP Note'}
                  </span>
                </button>

                <button
                  className="new-recording-btn"
                  onClick={handleNewRecording}
                  disabled={isGenerating}
                >
                  <span className="btn-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 4V20M20 12H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </span>
                  <span className="btn-text">
                    New Recording
                  </span>
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="error-message">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* SOAP Note Display */}
          {soapNote && (
            <SOAPNoteDisplay
              soapNote={soapNote}
              editedSoapNote={editedSoapNote}
              isEditing={isEditing}
              copied={copied}
              isSaved={isSaved}
              hasUnsavedChanges={hasUnsavedChanges}
              isRegeneratingSupport={isRegeneratingSupport}
              isSavingHistory={isSavingHistory}
              patientName={patientName}
              appointmentDate={appointmentDate}
              fileName={selectedFile?.name || recordedAudio?.name}
              audioFile={selectedFile || recordedAudio}
              editingCodeIndex={editingCodeIndex}
              editedCodeValue={editedCodeValue}
              setEditedCodeValue={setEditedCodeValue}
              editedCodeDescription={editedCodeDescription}
              setEditedCodeDescription={setEditedCodeDescription}
              editedCodeTiming={editedCodeTiming}
              setEditedCodeTiming={setEditedCodeTiming}
              handleEditSOAP={handleEditSOAP}
              handleSaveEdit={handleSaveEdit}
              handleCancelEdit={handleCancelEdit}
              handleEditField={handleEditField}
              handleCloseSoapNote={handleCloseSoapNote}
              handleCopySOAP={handleCopySOAP}
              startEditingCode={startEditingCode}
              saveEditedCode={saveEditedCode}
              cancelEditingCode={cancelEditingCode}
              addNewCode={addNewCode}
              deleteCode={deleteCode}
              saveToHistory={saveToHistory}
              currentUser={currentUser}
            />
          )}
        </div>

        {/* History Modal */}
        {showHistory && (
          <div className="history-modal-overlay">
            <div className="history-modal">
              {/* Modal Header */}
              <div className="history-modal-header">
                <div className="history-header-left">
                  <h2 className="history-title">SOAP Note History</h2>
                  <button
                    className={`filter-toggle history-filter-toggle ${showFilters ? 'active' : ''}`}
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 4C3 3.44772 3.44772 3 4 3H20C20.5523 3 21 3.44772 21 4V6.58579C21 6.851 20.8946 7.10536 20.7071 7.29289L14.2929 13.7071C14.1054 13.8946 14 14.149 14 14.4142V17L10 21V14.4142C10 14.149 9.89464 13.8946 9.70711 13.7071L3.29289 7.29289C3.10536 7.10536 3 6.851 3 6.58579V4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {showFilters ? 'Hide filters' : 'Filter'}
                  </button>
                </div>
                <button
                  className="history-close"
                  onClick={() => setShowHistory(false)}
                  aria-label="Close history"
                >
                  ×
                </button>
              </div>

              {/* Filter Section */}
              {showFilters && (
                <div className="history-filter-panel">
                  <div className="history-filter-grid">
                    {/* Patient Name Filter */}
                    <div className="history-filter-field">
                      <label className="field-label">Patient Name</label>
                      <input
                        type="text"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        placeholder="Search by name..."
                        className="field-input history-filter-input"
                      />
                    </div>

                    {/* Date From Filter */}
                    <div className="history-filter-field">
                      <label className="field-label">From Date</label>
                      <input
                        type="date"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                        className="field-input history-filter-input"
                      />
                    </div>

                    {/* Date To Filter */}
                    <div className="history-filter-field">
                      <label className="field-label">To Date</label>
                      <input
                        type="date"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                        className="field-input history-filter-input"
                      />
                    </div>
                  </div>

                  {/* Filter Actions */}
                  <div className="history-filter-actions">
                    <button
                      className="portal-ghost history-filter-reset"
                      onClick={clearFilters}
                    >
                      Clear Filters
                    </button>
                    <div className="history-results-chip">
                      {getFilteredHistory().length} result{getFilteredHistory().length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              )}

              {/* Modal Content */}
              <div className="history-modal-body">
                {soapHistory.length === 0 ? (
                  <div className="history-empty">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 12H15M9 16H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L18.7071 8.70711C18.8946 8.89464 19 9.149 19 9.41421V19C19 20.1046 18.1046 21 17 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <h3>No saved notes yet</h3>
                    <p>Generate and save your first SOAP note to see it here.</p>
                  </div>
                ) : getFilteredHistory().length === 0 ? (
                  <div className="history-empty">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <h3>No results found</h3>
                    <p>Adjust your filters to find more results.</p>
                  </div>
                ) : (
                  <div className="history-grid">
                    {getFilteredHistory().map((note) => (
                      <div
                        key={note.id}
                        className={`history-card ${isLoadingHistoryNote ? 'is-disabled' : ''}`}
                        onClick={() => {
                          if (!isLoadingHistoryNote) {
                            handleLoadNote(note);
                          }
                        }}
                      >
                        {/* Delete button */}
                        <button
                          className="history-delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNote(note);
                          }}
                          aria-label="Delete history note"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>

                        <div className="history-card-header">
                          <div>
                            <h3>{note.patientName || 'Unnamed Patient'}</h3>
                            {note.appointmentDate && (
                              <p>
                                {(() => {
                                  const [year, month, day] = note.appointmentDate.split('-');
                                  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  });
                                })()}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="history-card-meta">
                          <p>
                            <strong>Created:</strong>{' '}
                            {note.createdAt ? new Date(note.createdAt).toLocaleString() : 'Unknown'}
                          </p>
                          {note.fileName && (
                            <p>
                              <strong>Audio:</strong> {note.fileName}
                            </p>
                          )}
                        </div>

                        {/* Content indicators */}
                        <div className="history-card-tags">
                          {note.audioBase64 && (
                            <span className="history-tag history-tag-audio">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                                <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                              </svg>
                              Audio
                            </span>
                          )}
                          {note.soapNote?.soap_note && (
                            <span className="history-tag history-tag-soap">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/>
                              </svg>
                              SOAP Note
                            </span>
                          )}
                          {note.soapNote?.clinical_support && (
                            <span className="history-tag history-tag-ai">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                              </svg>
                              AI Support
                            </span>
                          )}
                          {note.soapNote?.pii_redacted && (
                            <span className="history-tag history-tag-pii">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                              </svg>
                              PII Protected
                            </span>
                          )}
                        </div>

                        <div className="history-card-preview">
                          <strong>Preview:</strong>
                          <p>
                            {note.soapNote?.soap_note?.subjective?.substring(0, 150) || 'No content available'}...
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="footer">
          <p>Built for HIPAA/PIPEDA compliance by design — awaiting deployment to BAA-certified infrastructure.</p>
        </div>

        {/* Add Patient Modal */}
        {showPatientModal && (
          <AddPatientModal
            newPatientData={newPatientData}
            setNewPatientData={setNewPatientData}
            patientModalError={patientModalError}
            onConfirm={handleCreatePatient}
            onCancel={() => {
              setShowPatientModal(false);
              setNewPatientData({ fullName: '', email: '', dateOfBirth: '', phone: '' });
              setPatientModalError('');
            }}
          />
        )}

        {/* Delete Note Confirmation Modal */}
        {showDeleteNoteModal && (
          <DeleteNoteModal
            noteToDelete={noteToDelete}
            onConfirm={confirmDeleteNote}
            onCancel={cancelDeleteNote}
          />
        )}

        {/* Save Success Modal */}
        {showSaveSuccessModal && (
          <SaveSuccessModal onClose={() => setShowSaveSuccessModal(false)} />
        )}

        {/* Close Confirmation Modal */}
        {showCloseConfirmModal && (
          <ConfirmCloseModal
            onConfirm={confirmCloseSoapNote}
            onCancel={() => setShowCloseConfirmModal(false)}
          />
        )}

        {/* Generic Success Modal */}
        {showSuccessModal && (
          <GenericSuccessModal
            message={successMessage}
            onClose={() => setShowSuccessModal(false)}
          />
        )}

        {/* Generic Error Modal */}
        {showErrorModal && (
          <GenericErrorModal
            message={errorMessage}
            onClose={() => setShowErrorModal(false)}
          />
        )}

        {/* Session Expired Modal */}
        {showSessionExpiredModal && (
          <SessionExpiredModal
            onLogin={() => {
              setShowSessionExpiredModal(false);
              handleLogout();
            }}
          />
        )}

        {/* Edit Patient Modal */}
        {showEditPatientModal && (
          <EditPatientModal
            patientToEdit={patientToEdit}
            editPatientData={editPatientData}
            setEditPatientData={setEditPatientData}
            editPatientError={editPatientError}
            onConfirm={confirmEditPatient}
            onCancel={cancelEditPatient}
          />
        )}

        {/* Pricing Modal */}
        {showPricingModal && (
          <div className="pricing-modal-overlay" onClick={() => setShowPricingModal(false)}>
            <div className="pricing-modal" onClick={(e) => e.stopPropagation()}>
              <button className="pricing-modal-close" onClick={() => setShowPricingModal(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <div className="pricing-modal-header">
                <h2 className="pricing-modal-title">
                  {isSubscriptionEnded
                    ? 'Upgrade to Continue'
                    : 'Choose Your Plan'}
                </h2>
                <p className="pricing-modal-subtitle">
                  {isSubscriptionEnded
                    ? 'Your free trial has ended. Choose a plan to continue using AlgoDentra.'
                    : 'Start your 14-day free trial today. No credit card required.'}
                </p>
              </div>

              <div className="pricing-plans">
                {/* Basic Plan */}
                <div className="pricing-plan">
                  <div className="pricing-plan-header">
                    <h3 className="pricing-plan-name">Basic</h3>
                    <div className="pricing-plan-price">
                      <span className="pricing-plan-amount">$20</span>
                      <span className="pricing-plan-period">/month</span>
                    </div>
                  </div>
                  <ul className="pricing-plan-features">
                    <li className="pricing-plan-feature">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      5 maximum SOAP notes per day
                    </li>
                    <li className="pricing-plan-feature">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Unlimited patients, AI decision support, patient portal
                    </li>
                    <li className="pricing-plan-feature">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Email support (48hr response), audit logs
                    </li>
                    <li className="pricing-plan-feature">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Compliance: PIPEDA/HIPAA compliant
                    </li>
                    <li className="pricing-plan-feature pricing-plan-feature-note">
                      Best for: Solo dentists, new graduates
                    </li>
                  </ul>
                  <button
                    className="pricing-plan-button pricing-plan-button-secondary"
                    onClick={() => handleStartTrial('basic')}
                    disabled={isCheckoutLoading}
                  >
                    {isCheckoutLoading && checkoutPlan === 'basic'
                      ? 'Processing...'
                      : isSubscriptionEnded
                        ? 'Upgrade to Basic'
                        : 'Start Free Trial'}
                  </button>
                </div>

                {/* Pro Plan */}
                <div className="pricing-plan pricing-plan-featured">
                  <div className="pricing-plan-badge">Most Popular</div>
                  <div className="pricing-plan-header">
                    <h3 className="pricing-plan-name">Pro</h3>
                    <div className="pricing-plan-price">
                      <span className="pricing-plan-amount">$50</span>
                      <span className="pricing-plan-period">/month</span>
                    </div>
                  </div>
                  <ul className="pricing-plan-features">
                    <li className="pricing-plan-feature">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Unlimited SOAP notes
                    </li>
                    <li className="pricing-plan-feature">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Unlimited patients, AI decision support, patient portal
                    </li>
                    <li className="pricing-plan-feature">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Priority email support (24hr response), audit logs
                    </li>
                    <li className="pricing-plan-feature">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M13.5 4.5L6 12L2.5 8.5" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Compliance: PIPEDA/HIPAA compliant
                    </li>
                    <li className="pricing-plan-feature pricing-plan-feature-note">
                      Best for: Heavy usage, solo dentists & new graduates
                    </li>
                  </ul>
                  <button
                    className="pricing-plan-button pricing-plan-button-primary"
                    onClick={() => handleStartTrial('pro')}
                    disabled={isCheckoutLoading}
                  >
                    {isCheckoutLoading && checkoutPlan === 'pro'
                      ? 'Processing...'
                      : isSubscriptionEnded
                        ? 'Upgrade to Pro'
                        : 'Start Free Trial'}
                  </button>
                </div>
              </div>

              {/* Promo Code Section */}
              <div className="pricing-promo-divider">
                <span className="pricing-promo-divider-text">OR</span>
              </div>

              <div className="pricing-promo-section">
                <label className="pricing-promo-label">Have a promo code?</label>
                <div className="pricing-promo-input-wrapper">
                  <input
                    type="text"
                    className={`pricing-promo-input ${promoCodeError ? 'pricing-promo-input-error' : ''}`}
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => {
                      setPromoCode(e.target.value.toUpperCase());
                      setPromoCodeError('');
                    }}
                    disabled={isApplyingPromo}
                  />
                  <button
                    className="pricing-promo-button"
                    onClick={async () => {
                      if (!promoCode.trim()) {
                        setPromoCodeError('Please enter a promo code');
                        return;
                      }

                      setIsApplyingPromo(true);
                      setPromoCodeError('');

                      try {
                        // Redeem promo code
                        const data = await billingApi.redeemPromoCode(promoCode);

                        if (data.success) {
                          // Success! Close modal and show success notification
                          setShowPricingModal(false);
                          setPromoCode('');
                          setPromoCodeError('');

                          // Refresh subscription status
                          await fetchSubscriptionStatus();

                          // Show success notification
                          setNotificationModal({
                            show: true,
                            type: 'success',
                            title: 'Promo Code Redeemed!',
                            message: data.message || 'Your promo code has been successfully applied.',
                          });
                        } else {
                          // Show error
                          setPromoCodeError(data.error || 'Invalid promo code');
                        }
                      } catch (error) {
                        console.error('Promo code redemption error:', error);
                        setPromoCodeError('Failed to validate promo code. Please try again.');
                      } finally {
                        setIsApplyingPromo(false);
                      }
                    }}
                    disabled={isApplyingPromo || !promoCode.trim()}
                  >
                    {isApplyingPromo ? 'Validating...' : 'Apply'}
                  </button>
                </div>
                {promoCodeError && (
                  <p className="pricing-promo-error">{promoCodeError}</p>
                )}
              </div>

              <p className="pricing-modal-footer">
                Cancel anytime during your trial. No questions asked.
              </p>
            </div>
          </div>
        )}

        {/* Password Change Confirmation Modal */}
        {showPasswordChangeModal && (
          <PasswordChangeModal
            userEmail={currentUser?.email}
            onConfirm={confirmChangePassword}
            onCancel={() => setShowPasswordChangeModal(false)}
          />
        )}

        {/* Cancel Subscription Confirmation Modal */}
        {showCancelSubscriptionModal && (
          <CancelSubscriptionModal
            isLoading={isCancelingSubscription}
            onConfirm={() => {
              setShowCancelSubscriptionModal(false);
              handleCancelSubscription();
            }}
            onCancel={() => setShowCancelSubscriptionModal(false)}
          />
        )}

        {/* Notification Modal */}
        <NotificationModal
          notificationModal={notificationModal}
          setNotificationModal={setNotificationModal}
        />

        {/* Embedded Checkout Modal */}
        {showEmbeddedCheckout && (
          <EmbeddedCheckout
            plan={checkoutPlan}
            onSuccess={async () => {
              setShowEmbeddedCheckout(false);
              const planName = checkoutPlan;
              setCheckoutPlan(null);

              // Refresh subscription status with retry logic
              // (webhook may take a few seconds to process)
              const refreshWithRetry = async (maxAttempts = 5, delay = 1000) => {
                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                  try {
                    const data = await billingApi.getStatus();
                    if (data.success) {
                      setSubscriptionStatus(data);
                      // Check if status is now active
                      if (data.subscription?.status === 'active') {
                        return true; // Success - subscription is active
                      }
                    }
                  } catch (err) {
                    console.error(`Error refreshing subscription status (attempt ${attempt + 1}):`, err);
                  }

                  // Wait before next attempt (if not last attempt)
                  if (attempt < maxAttempts - 1) {
                    await new Promise(resolve => setTimeout(resolve, delay));
                  }
                }
                return false; // Failed to get active status
              };

              // Try to refresh status
              refreshWithRetry();

              // Show success notification
              setNotificationModal({
                show: true,
                type: 'success',
                title: 'Subscription Activated!',
                message: `Your ${planName === 'basic' ? 'Basic' : 'Pro'} subscription is now active. Welcome aboard!`
              });
            }}
            onCancel={() => {
              setShowEmbeddedCheckout(false);
              setCheckoutPlan(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

// PaymentSuccess and PaymentFailed now imported from pages/
// (Removed ~100 lines - extracted to separate page components)

// Router wrapper component
function App() {
  // Get Google OAuth client ID from environment variable
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<MainApp />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/failed" element={<PaymentFailed />} />
        <Route path="/verify" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/demo" element={<Demo />} />
        <Route path="/*" element={<MainApp />} />
      </Routes>
    </GoogleOAuthProvider>
  );
}

export default App;
